import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { selectActiveAchInstructions, selectInfo, selectNraIndicator, selectStateCode } from 'projects/money-movement-ui/src/app/account-info-header/reducers';
import { Constants, amountTypeDict, frequencyDict, pageInContext, transactionDetailLabels, transactionDirection, transactionTypes, transactionTypesDict } from 'projects/money-movement-ui/src/app/shared/utilities/Constants';
import { selectIsStandingAuthViewPage, selectIsTransactionsViewPage, selectViewFrequency } from 'projects/money-movement-ui/src/app/transaction-view/reducers';
import { Observable, Subject, combineLatest } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { selectViewInContext } from '../../../shared/reducers';
import { StandingAuthReviewPageActions } from '../../actions';
import { isAchFormSA } from '../../models/standingAuthorizationForm';
import { selectAchInfoToFromAccountSA, selectMoneyLinkType, selectStagedStandingAuthForm, selectStandingAuthAchInfo, selectStandingAuthIdSA } from '../../reducers';

@Component({
  standalone: false,
  selector: 'mm-sa-transaction-details-section-ach',
  templateUrl: './sa-transaction-details-section-ach.component.html',
  styleUrls: ['./sa-transaction-details-section-ach.component.scss']
})
export class SATransactionDetailsSectionAchComponent implements OnInit, OnDestroy {
  @Input() isInrFromTable: boolean;
  readonly labels = transactionDetailLabels;
  readonly frequencyTypes = frequencyDict;
  readonly taxCopyParagraphOne: string = Constants.taxCopyParagraphOne;
  readonly taxCopyParagraphTwo: string = Constants.taxCopyParagraphTwo;
  fundsAvailableToTradeCopy: string = Constants.fundsAvailableToTrade;
  transactionTypeLabel: string = transactionTypesDict[transactionTypes.ach];
  transferDirection: string;
  nextWithdrawalDate: string;
  nextCompletionDate: string;
  withdrawalDateCopy: string = Constants.expectedWithdrawalDateCopy;
  amount: string;
  isIra: boolean;
  contributionReason: string;
  distributionReason: string;
  selectedAuthorization: string;
  nickname: string;
  to1: string;
  to2: string;
  fedTaxPercent: string;
  fedTaxAmount: string;
  stateTaxPercent: string;
  stateTaxAmount: string;
  createRequestStep$: Observable<string>;
  frequency: string | undefined;
  destroy$: Subject<boolean> = new Subject<boolean>();
  isStandingAuthViewPage: boolean = false;
  isTransactionViewPage: boolean = false;
  endDate: string;
  secondCompletionDate: string;
  isThirdParty: boolean = false;
  transactionType: string;
  moneyLinkType: string;
  fundsAvailableToTrade: string;
  accountName: string;
  accountType: string;
  abaNumber: string;
  saStateTaxLabel: string = transactionDetailLabels.stateTax;
  amountType: string;
  isNra: boolean;
  netAmount: string;
  grossAmount:string;

  constructor(private store: Store) {
  }

  ngOnInit(): void {

    this.store.select(selectInfo).pipe(takeUntil(this.destroy$)).subscribe(info => {
      this.isIra = info.classifications.isIra;
    });

    combineLatest([
      this.store.pipe(select(selectStagedStandingAuthForm)),
      this.store.pipe(select(selectStandingAuthAchInfo)),
      this.store.select(selectStateCode),
      this.store.select(selectNraIndicator)
    ]).pipe(takeUntil(this.destroy$),
    ).subscribe(([form, achInfo, stateCode, nraIndicator]) => {
      if (form?.transactionType && isAchFormSA(form)) {
        const extraPrompts = form.extraPrompts;
        const standingAuthorizationDetails = form.standingAuthorizationDetails;
        this.transferDirection = extraPrompts.transferDirection;
        this.nextWithdrawalDate = standingAuthorizationDetails.withdrawalDate;
        this.nextCompletionDate = achInfo.processDate;
        this.contributionReason = standingAuthorizationDetails.contributionReason;
        this.frequency = achInfo?.frequencyList ? achInfo.frequencyList.find(frequency => frequency.code === standingAuthorizationDetails.recurringFrequency)?.description! : standingAuthorizationDetails.recurringFrequency;
        this.endDate = standingAuthorizationDetails.endDate;
        this.secondCompletionDate = standingAuthorizationDetails.secondProcessDate;
        this.fedTaxPercent = achInfo?.fedTaxPercent ?? '';
        this.stateTaxPercent = achInfo?.stateTaxPercent ?? '';
        this.amount = form.standingAuthorizationDetails.amountType === amountTypeDict.net ? achInfo.transferAmount : achInfo.withdrawalAmount;
        this.grossAmount = achInfo.withdrawalAmount
        this.fundsAvailableToTrade = form.standingAuthorizationDetails.fundsAvailableToTrade;
        this.amountType = form.standingAuthorizationDetails.amountType;
        this.isNra = nraIndicator ?? false;
      }

      if (stateCode && !nraIndicator) {
        this.saStateTaxLabel = `${this.labels.stateTax} (${stateCode})`;
      }
    });

    this.store.select(selectStandingAuthIdSA).pipe(takeUntil(this.destroy$)).subscribe(selectedAuthorization => {
      this.selectedAuthorization = selectedAuthorization?.split('-')[1];
    });

    this.store
      .select(selectIsStandingAuthViewPage)
      .pipe(takeUntil(this.destroy$))
      .subscribe(isStandingAuthViewPage => this.isStandingAuthViewPage = isStandingAuthViewPage);

    this.store
      .select(selectIsTransactionsViewPage)
      .pipe(takeUntil(this.destroy$))
      .subscribe(isTransactionViewPage => this.isTransactionViewPage = isTransactionViewPage);

      combineLatest([
        this.store.select(selectActiveAchInstructions),
        this.store.select(selectAchInfoToFromAccountSA),
        this.store.select(selectViewFrequency),
        this.store.select(selectViewInContext)
      ])
        .pipe(takeUntil(this.destroy$))
        .subscribe(([instructions, toFromAccount, frequency, step]) => {
          instructions.forEach((instruction) => {
            this.to1 = toFromAccount.financialInstitution;
            this.accountName = `Account name: ${toFromAccount.accountName}`;
            this.to2 = toFromAccount.accountNumber
              ? `Account number: ${toFromAccount.accountNumber}`
              : '';
            this.accountType = `Account type: ${toFromAccount.accountType}`;
            this.abaNumber = `ABA number: ${toFromAccount.abaNumber}`
            if (step === pageInContext.view) {
              this.frequency = frequency;
            }

            if (instruction.cashieringRqstId === this.selectedAuthorization) {
              this.nickname = instruction.nickname;
              this.isThirdParty = instruction.isThirdParty;
              if (this.isThirdParty) {
                this.transactionType = `${this.transactionTypeLabel} - 3rd party`;
              } else {
                this.transactionType = `${this.transactionTypeLabel} - 1st party`;
              }
            }
          });
        });

    this.store
      .select(selectMoneyLinkType)
      .pipe(takeUntil(this.destroy$),
      filter( _ => this.isStandingAuthViewPage || this.isTransactionViewPage))
      .subscribe(moneyLinkType => {
        this.moneyLinkType = moneyLinkType;
    });

    this.store.select(selectStandingAuthAchInfo).pipe(takeUntil(this.destroy$)).subscribe(info => {
      this.fedTaxAmount = info.fedTaxAmount?.split(' ')[0];
      this.stateTaxAmount = info.stateTaxAmount?.split(' ')[0];
      this.netAmount = info.transferAmount;
      this.grossAmount = info.withdrawalAmount;
      this.distributionReason = info.distributionReason;
    });
    this.createRequestStep$ = this.store.select(selectViewInContext);
  }


  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  onEditClicked(): void {
    this.store.dispatch(StandingAuthReviewPageActions.editClicked());
  }

  get isRecurring() {
    // frequency is not set on the transaction flow so this.frequency is needed to not show the end date on the review page
    return this.frequency && this.frequency !== 'On-Request';
  }

  get isAchIn() {
    return this.transferDirection === transactionDirection.in.toLowerCase()
  }
}